import React, {type ReactNode, type RefObject, useState} from 'react'
import GeneralButton from '@/Components/Buttons/GeneralButton'
import FormattedText from '@/Pages/Events/Assistant/FormattedText'
import LinkedText from '@/Pages/Events/Assistant/LinkedText'
import {
    approvalCopy,
    offersSendLater,
    removesUsabilityExtension,
    sendLaterSentence,
    type AssistantApproval,
} from '@/Pages/Events/Assistant/assistantTranscript'

const tournamentLabel = (tool?: string, reason?: string): string | null => {
    if (tool === 'create_event') {
        return 'Create tournament'
    }

    if (tool === 'update_event') {
        return (reason ?? '').startsWith('Publish ') ? 'Publish' : 'Save'
    }

    if (tool === 'create_ticket_type') {
        return 'Add ticket'
    }

    if (tool === 'create_short_url') {
        return 'Create link'
    }

    if (tool === 'update_ticket') {
        return removesUsabilityExtension(reason) ? 'Remove' : 'Extend'
    }

    if (tool === 'create_validation') {
        return 'Mark used'
    }

    return null
}

const refundPercentValue = (value: string): number | null => {
    if (!/^\d{1,2}$/.test(value)) {
        return null
    }

    const percent = Number(value)

    return percent >= 1 && percent <= 99 ? percent : null
}

const ApprovalCopy = ({reason, confirming, plain = false, formatted = false}: {reason?: string, confirming: boolean, plain?: boolean, formatted?: boolean}): ReactNode => {
    if (plain) {
        const text = (reason ?? '').trim()

        if (text === '') {
            return null
        }

        return formatted ? (
            <FormattedText className="break-words text-xs leading-5" text={text}/>
        ) : (
            <LinkedText className="whitespace-pre-wrap break-words text-xs leading-5" text={text}/>
        )
    }

    const copy = approvalCopy(reason)

    return (
        <div className="space-y-2">
            {copy.summary !== '' && <LinkedText className="whitespace-pre-wrap break-words text-xs leading-5" text={copy.summary}/>}
            {confirming && copy.when && (
                <p data-testid="event-assistant-send-at">{sendLaterSentence(copy.when)}</p>
            )}
            {!confirming && copy.note && <LinkedText className="text-xs leading-5 text-storm-30" text={copy.note}/>}
        </div>
    )
}

interface Props {
    approval: AssistantApproval
    index: number
    asking: boolean
    firstRef?: RefObject<HTMLDivElement | null>
    decide: (id: string, action: 'approve' | 'reject', sendLater?: boolean, percentage?: number) => void
}

const EventAssistantApproval = ({
    approval,
    index,
    asking,
    firstRef,
    decide,
}: Props): ReactNode => {
    const [scheduling, setScheduling] = useState(false)
    const [partial, setPartial] = useState(false)
    const [percentText, setPercentText] = useState('')
    const resend = approval.tool === 'resend_sale'
    const pass = approval.tool === 'AddCoachPass' || approval.tool === 'CreateCoach' || approval.tool === 'create_sale' || approval.tool === 'create_ticket'
    const refund = approval.tool === 'RefundSale'
    const tournament = tournamentLabel(approval.tool, approval.reason)
    const settled = approval.settled === true
    const locked = settled || asking
    const later = !resend && !pass && !refund && tournament === null && offersSendLater(approval.reason)
    const percentId = index === 0 ? 'event-assistant-refund-percent' : `event-assistant-refund-percent-${approval.id}`
    const percent = refundPercentValue(percentText)

    return (
        <div
            ref={firstRef}
            data-testid="event-assistant-approval"
            className="mb-2 space-y-2"
        >
            <span className="sr-only">Approval needed. </span>
            <ApprovalCopy
                reason={approval.reason}
                confirming={scheduling}
                plain={resend || pass || refund || tournament !== null}
                formatted={resend}
            />
            <div className="grid grid-cols-2 gap-2">
                {refund && partial ? (
                    <>
                        <label className="col-span-2 block text-xs font-medium text-gray-900" htmlFor={percentId}>
                            Percent
                            <input
                                id={percentId}
                                data-testid={percentId}
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                value={percentText}
                                onChange={(event) => {
                                    setPercentText(event.target.value.replace(/\D/g, '').slice(0, 2))
                                }}
                                className="mt-1 w-full rounded-md border border-storm-50 px-2 py-1 text-sm text-gray-900"
                            />
                        </label>
                        <p className="col-span-2 text-xs leading-5 text-storm-30">
                            {percent === null
                                ? 'Enter a percent from 1 to 99.'
                                : `Refund ${percent}% of that full refund. The amount is calculated when you confirm.`}
                        </p>
                        <GeneralButton
                            nativeType="button"
                            theme="primary"
                            className="w-full"
                            testId={index === 0 ? 'event-assistant-refund-partial' : `event-assistant-refund-partial-${approval.id}`}
                            disabled={locked || percent === null}
                            onClick={() => {
                                if (!locked && percent !== null) {
                                    void decide(approval.id, 'approve', false, percent)
                                }
                            }}
                        >
                            <>
                                <span className="text-xs font-semibold tracking-normal">
                                    {percent === null ? 'Refund' : `Refund ${percent}%`}
                                </span>
                                <span className="sr-only">{`, request ${index + 1}`}</span>
                            </>
                        </GeneralButton>
                        <GeneralButton
                            nativeType="button"
                            theme="secondary"
                            className="w-full"
                            testId={index === 0 ? 'event-assistant-refund-back' : `event-assistant-refund-back-${approval.id}`}
                            disabled={locked}
                            onClick={() => {
                                if (locked) {
                                    return
                                }

                                setPartial(false)
                            }}
                        >
                            <>
                                <span className="text-xs font-semibold tracking-normal">Back</span>
                                <span className="sr-only">{`, request ${index + 1}`}</span>
                            </>
                        </GeneralButton>
                    </>
                ) : scheduling && !(approval.tool === 'CoachImport' && settled) ? (
                    <>
                        <GeneralButton
                            nativeType="button"
                            theme="primary"
                            className="w-full"
                            testId={index === 0 ? 'event-assistant-schedule' : `event-assistant-schedule-${approval.id}`}
                            disabled={locked}
                            onClick={() => {
                                if (!locked) {
                                    void decide(approval.id, 'approve', true)
                                }
                            }}
                        >
                            <>
                                Schedule
                                <span className="sr-only">{`, request ${index + 1}`}</span>
                            </>
                        </GeneralButton>
                        <GeneralButton
                            nativeType="button"
                            theme="secondary"
                            className="w-full"
                            testId={index === 0 ? 'event-assistant-back' : `event-assistant-back-${approval.id}`}
                            disabled={locked}
                            onClick={() => {
                                if (locked) {
                                    return
                                }

                                setScheduling(false)
                            }}
                        >
                            <>
                                Back
                                <span className="sr-only">{`, request ${index + 1}`}</span>
                            </>
                        </GeneralButton>
                    </>
                ) : (
                    <>
                        <GeneralButton
                            nativeType="button"
                            theme="primary"
                            className="col-span-2 w-full"
                            testId={index === 0 ? 'event-assistant-approve' : `event-assistant-approve-${approval.id}`}
                            disabled={locked}
                            onClick={() => {
                                if (!locked) {
                                    void decide(approval.id, 'approve')
                                }
                            }}
                        >
                            <>
                                {refund ? 'Refund' : resend ? 'Send tickets' : pass ? 'Send pass' : tournament ?? 'Send now'}
                                <span className="sr-only">{`, request ${index + 1}`}</span>
                            </>
                        </GeneralButton>
                        {refund && (
                            <GeneralButton
                                nativeType="button"
                                theme="secondary"
                                className="w-full"
                                testId={index === 0 ? 'event-assistant-partial' : `event-assistant-partial-${approval.id}`}
                                disabled={locked}
                                onClick={() => {
                                    if (locked) {
                                        return
                                    }

                                    setPartial(true)
                                }}
                            >
                                <>
                                    Partial
                                    <span className="sr-only">{`, request ${index + 1}`}</span>
                                </>
                            </GeneralButton>
                        )}
                        {later && (
                            <GeneralButton
                                nativeType="button"
                                theme="secondary"
                                className="w-full"
                                testId={index === 0 ? 'event-assistant-send-later' : `event-assistant-send-later-${approval.id}`}
                                disabled={locked}
                                onClick={() => {
                                    if (locked) {
                                        return
                                    }

                                    setScheduling(true)
                                }}
                            >
                                <>
                                    Send later
                                    <span className="sr-only">{`, request ${index + 1}`}</span>
                                </>
                            </GeneralButton>
                        )}
                        <GeneralButton
                            nativeType="button"
                            theme="secondary"
                            className={later || refund ? 'w-full' : 'col-span-2 w-full'}
                            testId={index === 0 ? 'event-assistant-cancel' : `event-assistant-cancel-${approval.id}`}
                            disabled={locked}
                            onClick={() => {
                                if (!locked) {
                                    void decide(approval.id, 'reject')
                                }
                            }}
                        >
                            <>
                                Cancel
                                <span className="sr-only">{`, request ${index + 1}`}</span>
                            </>
                        </GeneralButton>
                    </>
                )}
            </div>
        </div>
    )
}

export default EventAssistantApproval
